import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { HelpCircle, Search, MessageCircle, Book, Phone, Mail, ChevronRight, Ticket } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, Button, StatusPill, EmptyState } from '../components/ui';

const HelpCenter = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [tickets, setTickets] = useState([]);

  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('topgame_tickets') || '[]');
      setTickets(Array.isArray(stored) ? stored : []);
    } catch {
      setTickets([]);
    }
  }, []);

  const categories = [
    { id: 'all', name: 'All Topics', icon: Book },
    { id: 'booking', name: 'Booking', icon: MessageCircle },
    { id: 'payment', name: 'Payment', icon: Phone },
    { id: 'account', name: 'Account', icon: HelpCircle }
  ];

  const faqs = [
    {
      id: 1,
      category: 'booking',
      question: 'How do I book a cricket match?',
      answer: 'Navigate to the Services page, select a ground, choose your date and time slot, fill in team details, and confirm your booking. You can book for a single team or both teams.'
    },
    {
      id: 2,
      category: 'booking',
      question: 'Can I cancel my booking?',
      answer: 'Yes, you can cancel your booking from the "My Bookings" page. Click on the booking you want to cancel and select the cancel option. Please note cancellation policies may apply.'
    },
    {
      id: 3,
      category: 'booking',
      question: 'What is the difference between individual team booking and both team booking?',
      answer: 'Individual team booking allows you to book for one team only (Team A or Team B). Both team booking allows you to book for both teams in a single transaction.'
    },
    {
      id: 4,
      category: 'payment',
      question: 'What payment methods are accepted?',
      answer: 'We accept both cash and online payments. You can pay the full amount or make an advance payment. The balance amount can be paid later.'
    },
    {
      id: 5,
      category: 'payment',
      question: 'How do I pay the balance amount?',
      answer: 'You can pay the balance amount from the "My Bookings" page. Select your booking and click on "Pay Balance" to complete the payment.'
    },
    {
      id: 6,
      category: 'account',
      question: 'How do I view my booking history?',
      answer: 'Go to "My Bookings" page to view all your bookings. You can filter by service type and see future, current, and archived bookings.'
    }
  ];

  const filteredFAQs = faqs.filter(faq => {
    const matchesSearch = faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         faq.answer.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || faq.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Map local ticket status to a StatusPill colour key while keeping a readable label.
  const TICKET_STATUS = {
    open: 'requested',
    in_progress: 'pending',
    resolved: 'completed',
    closed: 'cancelled'
  };
  const ticketLabel = (status) =>
    (status || 'open').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-ga-text-primary mb-2 flex items-center gap-3">
            <HelpCircle className="h-8 w-8 text-ga-primary" />
            Help Center
          </h1>
          <p className="text-ga-text-muted">
            Find answers to common questions and get support
          </p>
        </div>

        {/* Hero CTA */}
        <Card className="mb-8 bg-ga-primary-soft border-ga-primary/30">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold text-ga-text-primary mb-1">Need a hand?</h2>
              <p className="text-ga-text-muted">
                Can't find your answer below? Raise a support ticket and our team will help you out.
              </p>
            </div>
            <Link to="/dashboard/create-ticket" className="ga-btn-primary inline-flex items-center gap-2 px-5 py-2.5 rounded-ga-md shrink-0">
              <MessageCircle className="h-5 w-5" />
              <span>Create ticket</span>
              <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
        </Card>

        {/* Search Bar */}
        <div className="mb-8">
          <div className="relative max-w-2xl mx-auto">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-ga-text-subtle h-5 w-5" />
            <input
              type="text"
              placeholder="Search for help..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="ga-input w-full rounded-ga-sm pl-12 pr-4 py-3"
            />
          </div>
        </div>

        {/* Categories */}
        <div className="mb-8">
          <div className="flex flex-wrap gap-3 justify-center">
            {categories.map((category) => {
              const Icon = category.icon;
              return (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-ga-md border transition-colors ${
                    selectedCategory === category.id
                      ? 'bg-ga-primary-soft border-ga-primary text-ga-primary'
                      : 'bg-ga-bg-surface border-ga-border-subtle text-ga-text-muted hover:border-ga-primary/40'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{category.name}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* My Tickets */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-ga-text-primary mb-4 flex items-center gap-2">
            <Ticket className="h-6 w-6 text-ga-primary" />
            My Tickets
          </h2>
          {tickets.length > 0 ? (
            <div className="ga-card rounded-ga-md shadow-ga-soft overflow-hidden divide-y divide-ga-border-subtle">
              {tickets.map((ticket) => (
                <div key={ticket.id} className="flex items-center justify-between gap-3 p-4">
                  <div className="min-w-0">
                    <p className="font-medium text-ga-text-primary truncate">{ticket.subject || 'Untitled ticket'}</p>
                    <p className="text-sm text-ga-text-muted truncate">
                      {ticket.category ? `${ticket.category} · ` : ''}
                      {ticket.createdAt ? new Date(ticket.createdAt).toLocaleDateString() : ''}
                    </p>
                  </div>
                  <StatusPill
                    status={TICKET_STATUS[ticket.status] || ticket.status || 'requested'}
                    label={ticketLabel(ticket.status)}
                  />
                </div>
              ))}
            </div>
          ) : (
            <EmptyState
              card
              icon={Ticket}
              title="No tickets yet"
              description="When you raise a support ticket it will appear here so you can track its status."
              action={(
                <Button as={Link} to="/dashboard/create-ticket">
                  Create ticket
                </Button>
              )}
            />
          )}
        </div>

        {/* FAQs */}
        <div className="space-y-4 mb-8">
          {filteredFAQs.length > 0 ? (
            filteredFAQs.map((faq) => (
              <div key={faq.id} className="ga-card rounded-ga-md shadow-ga-soft border border-ga-border-subtle p-6">
                <h3 className="text-lg font-semibold text-ga-text-primary mb-2">{faq.question}</h3>
                <p className="text-ga-text-muted">{faq.answer}</p>
              </div>
            ))
          ) : (
            <EmptyState
              card
              icon={HelpCircle}
              title="No results found"
              description="Try a different search term or category."
            />
          )}
        </div>

        {/* Contact Support */}
        <Card className="bg-ga-primary-soft border-ga-primary/30">
          <h2 className="text-2xl font-bold text-ga-text-primary mb-4">Still need help?</h2>
          <p className="text-ga-text-muted mb-4">
            Can't find what you're looking for? Contact our support team.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link
              to="/dashboard/create-ticket"
              className="ga-btn-primary inline-flex items-center gap-2 px-4 py-2 rounded-ga-md"
            >
              <MessageCircle className="h-5 w-5" />
              <span>Create Support Ticket</span>
              <ChevronRight className="h-4 w-4" />
            </Link>
            <a
              href="mailto:support@groundaale.com"
              className="ga-btn-ghost inline-flex items-center gap-2 px-4 py-2 rounded-ga-md"
            >
              <Mail className="h-5 w-5" />
              <span>Email Support</span>
            </a>
            <a
              href="tel:+1234567890"
              className="ga-btn-ghost inline-flex items-center gap-2 px-4 py-2 rounded-ga-md"
            >
              <Phone className="h-5 w-5" />
              <span>Call Support</span>
            </a>
          </div>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default HelpCenter;
